import React, { useRef, useState, useEffect } from 'react';

const UploaderImagen = ({ codigo, tipo_imagen = 'equipo', detalle = '', onUploadSuccess }) => {
    const inputRef = useRef(null);
    const [imagenes, setImagenes] = useState([]); // { id, file, preview }
    const [estado, setEstado] = useState({ tipo: null, mensajes: [] });

    const MAX_FILES = 5;

    const handleSeleccion = (e) => {
        const files = Array.from(e.target.files);

        if (imagenes.length + files.length > MAX_FILES) {
            setEstado({ tipo: "error", mensajes: [`Máximo ${MAX_FILES} imágenes permitidas`] });
            return;
        }

        const nuevas = files.map(file => ({
            id: `${file.name}-${Date.now()}-${Math.random()}`, // ID único
            file,
            preview: URL.createObjectURL(file)
        }));

        setImagenes(prev => [...prev, ...nuevas]);
        setEstado({ tipo: null, mensajes: [] });

        // resetear input para permitir volver a elegir los mismos archivos
        e.target.value = "";
    };

    const limpiarSeleccion = () => {
        imagenes.forEach(img => URL.revokeObjectURL(img.preview)); // liberar memoria
        setImagenes([]);
        setEstado({ tipo: null, mensajes: [] });
        if (inputRef.current) inputRef.current.value = "";
    };

    const eliminarImagen = (id) => {
        const img = imagenes.find(im => im.id === id);
        if (img) URL.revokeObjectURL(img.preview);

        const nuevas = imagenes.filter(im => im.id !== id);
        setImagenes(nuevas);

        if (nuevas.length === 0 && inputRef.current) {
            inputRef.current.value = "";
        }
    };

    const subirImagenes = async () => {
        if (imagenes.length === 0) return;

        const formData = new FormData();
        imagenes.forEach(img => formData.append('imagen[]', img.file)); // usar file real
        formData.append('codigo', codigo);
        formData.append('tipo_imagen', tipo_imagen);
        formData.append('detalle', detalle);

        setEstado({ tipo: "cargando", mensajes: [] });

        try {
            const response = await fetch(`${process.env.REACT_APP_URL_API}imagen/cargar-imagen`, {
                method: 'POST',
                body: formData,
            });

            const resultado = await response.json();

            limpiarSeleccion();

            const mensajes = Array.isArray(resultado) ? resultado.map(r => r.mensaje) : [resultado.mensaje];
            const hayErrores = mensajes.some(msg => msg.toLowerCase().includes('error') || msg.toLowerCase().includes('no'));

            if (hayErrores) {
                setEstado({ tipo: "error", mensajes });
                setTimeout(() => setEstado({ tipo: null, mensajes: [] }), 4000);
            } else {
                setEstado({ tipo: "ok", mensajes: [] });
                setTimeout(() => setEstado({ tipo: null, mensajes: [] }), 2000);
            }

            if (typeof onUploadSuccess === 'function') {
                onUploadSuccess();
            }

        } catch (err) {
            setEstado({ tipo: "error", mensajes: ["Error al subir las imágenes."] });
            setTimeout(() => setEstado({ tipo: null, mensajes: [] }), 4000);
        }
    };

    const abrirSelector = (forzarCamara = false) => {
        if (inputRef.current) {
            if (forzarCamara) {
                inputRef.current.setAttribute('capture', 'environment');
            } else {
                inputRef.current.removeAttribute('capture');
            }
            inputRef.current.click();
        }
    };

    // Liberar memoria al desmontar
    useEffect(() => {
        return () => {
            imagenes.forEach(img => URL.revokeObjectURL(img.preview));
        };
    }, [imagenes]);

    return (
        <div className="mt-3">
            <div className="form-group">
                <button
                    className="btn btn-xs btn-outline-primary mr-2"
                    onClick={() => abrirSelector(false)}
                >
                    📁 Seleccionar desde galería
                </button>

                <button
                    className="btn btn-xs btn-outline-secondary"
                    onClick={() => abrirSelector(true)}
                >
                    📷 Tomar foto
                </button>

                <input
                    type="file"
                    accept="image/*"
                    multiple
                    ref={inputRef}
                    onChange={handleSeleccion}
                    style={{ display: 'none' }}
                />
            </div>

            {imagenes.length > 0 && (
                <div className="mb-3">
                    <label>Vista previa:</label>
                    <div className="d-flex flex-wrap">
                        {imagenes.map((img) => (
                            <div key={img.id} className="position-relative mr-2 mb-2">
                                <img
                                    src={img.preview}
                                    alt={img.file.name}
                                    className="img-thumbnail"
                                    style={{ height: '50px', width: '50px', objectFit: 'cover' }}
                                />
                                <button
                                    type="button"
                                    className="btn btn-xs btn-danger position-absolute"
                                    style={{ top: '2px', right: '2px', borderRadius: '50%' }}
                                    onClick={() => eliminarImagen(img.id)}
                                    title="Eliminar"
                                >
                                    &times;
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {imagenes.length > 0 && (
                <div className="mb-3">
                    <button className="btn btn-xs btn-primary" onClick={subirImagenes}>
                        Subir {imagenes.length} imagen{imagenes.length > 1 ? 'es' : ''}
                    </button>
                    <button className="btn btn-xs btn-warning ml-2" onClick={limpiarSeleccion}>
                        Limpiar selección
                    </button>
                </div>
            )}

            {estado.tipo && (
                <div className="mt-3">
                    <label>Estado:</label>
                    <ul className="list-group">
                        {estado.tipo === "cargando" && (
                            <li className="list-group-item d-flex align-items-center">
                                <div className="spinner-border spinner-border-sm mr-2 text-primary" role="status" />
                                Subiendo imágenes...
                            </li>
                        )}
                        {estado.tipo === "error" &&
                            estado.mensajes.map((msg, i) => (
                                <li key={i} className="list-group-item list-group-item-danger">
                                    {msg}
                                </li>
                            ))
                        }
                        {estado.tipo === "ok" && (
                            <li className="list-group-item list-group-item-success">
                                Imágenes subidas correctamente ✅
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default UploaderImagen;
